/**
 * Date Cascade Utilities
 *
 * When a campaign order or campaign end date is SHORTENED, the child dates
 * (line items, flight schedules) must be clamped down to the new end date.
 * Without this, the auto-extend ratchet (autoAdjustEndDatesFromFlightSchedule)
 * sees a flight ending later than the parent and force-writes the old, later
 * end date back into the line item, campaign, AND order — reverting the edit.
 *
 * These functions are pure (no form API calls) so they are unit-testable.
 * Clamping on extension is a no-op by construction, so callers can run them
 * unconditionally after any end-date change.
 */

import moment from 'moment-timezone';

/**
 * Clamp a date to a maximum end date.
 *
 * If `date` is after `maxEnd`, returns `maxEnd` (preserving its object
 * conventions — form dates are UTC moments). Otherwise returns `date`
 * unchanged (same reference).
 *
 * @param {string|Date|moment.Moment|null} date
 * @param {string|Date|moment.Moment|null} maxEnd
 * @returns {string|Date|moment.Moment|null}
 */
export const clampDateToEnd = (date, maxEnd) => {
  if (!date || !maxEnd) return date;
  return moment(date).isAfter(moment(maxEnd)) ? maxEnd : date;
};

/**
 * Clamp a single entity's startDate/endDate and flight schedule entries
 * (both `flightSchedule` and `flightSchedules` naming variants) to maxEnd.
 *
 * @param {Object} entity – line item or campaign
 * @param {string|Date|moment.Moment|null} maxEnd
 * @returns {Object} the entity, clamped (new object only when something changed)
 */
const clampEntityToEndDate = (entity, maxEnd) => {
  if (!entity || !maxEnd) return entity;

  const endDate = clampDateToEnd(entity.endDate, maxEnd);
  // Start date must not exceed the (possibly clamped) end date
  const startDate = clampDateToEnd(entity.startDate, endDate);

  const updated = { ...entity, startDate, endDate };

  // The codebase uses both naming variants — clamp whichever fields exist
  ['flightSchedule', 'flightSchedules'].forEach((field) => {
    if (Array.isArray(entity[field])) {
      updated[field] = entity[field].map((flight) => {
        const flightEndDate = clampDateToEnd(flight.endDate, maxEnd);
        const flightStartDate = clampDateToEnd(flight.startDate, flightEndDate);
        return { ...flight, startDate: flightStartDate, endDate: flightEndDate };
      });
    }
  });

  return updated;
};

/**
 * Clamp a campaign's line items and its own campaign-level flight schedule
 * entries to maxEnd. Does NOT touch the campaign's own startDate/endDate —
 * callers set those themselves.
 *
 * @param {Object} campaign
 * @param {string|Date|moment.Moment|null} maxEnd
 * @returns {Object} new campaign object with clamped children
 */
export const clampCampaignToEndDate = (campaign, maxEnd) => {
  if (!campaign || !maxEnd) return campaign;

  const updated = { ...campaign };

  if (Array.isArray(campaign.lineItems)) {
    updated.lineItems = campaign.lineItems.map((lineItem) =>
      clampEntityToEndDate(lineItem, maxEnd)
    );
  }

  ['flightSchedule', 'flightSchedules'].forEach((field) => {
    if (Array.isArray(campaign[field])) {
      updated[field] = campaign[field].map((flight) => {
        const flightEndDate = clampDateToEnd(flight.endDate, maxEnd);
        const flightStartDate = clampDateToEnd(flight.startDate, flightEndDate);
        return { ...flight, startDate: flightStartDate, endDate: flightEndDate };
      });
    }
  });

  return updated;
};

/**
 * Clamp every campaign's line items and campaign-level flight schedules
 * to maxEnd.
 *
 * @param {Array} campaigns
 * @param {string|Date|moment.Moment|null} maxEnd
 * @returns {Array} new campaigns array
 */
export const clampCampaignsToEndDate = (campaigns, maxEnd) => {
  if (!Array.isArray(campaigns) || !maxEnd) return campaigns;
  return campaigns.map((campaign) => clampCampaignToEndDate(campaign, maxEnd));
};

/**
 * Clamp a line items array to maxEnd.
 *
 * @param {Array} lineItems
 * @param {string|Date|moment.Moment|null} maxEnd
 * @returns {Array} new line items array
 */
export const clampLineItemsToEndDate = (lineItems, maxEnd) => {
  if (!Array.isArray(lineItems) || !maxEnd) return lineItems;
  return lineItems.map((lineItem) => clampEntityToEndDate(lineItem, maxEnd));
};
